import type { JoyfulExtensionExternalMessage } from '@joyful/utils'
import { env } from '@/env'

interface ExtensionMessageResponse {
  ok?: boolean
  source?: string
}

interface ChromeRuntime {
  lastError?: { message?: string }
  sendMessage: (
    extensionId: string,
    message: JoyfulExtensionExternalMessage,
    callback?: (response: ExtensionMessageResponse | undefined) => void,
  ) => void
}

// `Window.chrome` is a Chromium-only global that lib.dom doesn't declare.
// We augment it locally (rather than reaching for an `as` cast that
// `typescript/consistent-type-assertions` blocks) so the lookup
// stays typed without pulling in `@types/chrome` for one optional field.
declare global {
  interface Window {
    chrome?: { runtime?: ChromeRuntime }
  }
}

const JOYFUL_EXTENSION_IDS = [
  env.VITE_JOYFUL_SIDECAR_EXTENSION_ID,
  env.VITE_JOYFUL_VAULT_EXTENSION_ID,
]

// Posts a message to the Joyful Chrome extension. No-op outside Chromium or
// when the extension isn't installed.
export function notifyJoyfulExtension(message: JoyfulExtensionExternalMessage) {
  const runtime = window.chrome?.runtime
  if (!runtime) {
    return
  }

  for (const extensionId of new Set(JOYFUL_EXTENSION_IDS)) {
    runtime.sendMessage(extensionId, message, () => {
      // Reading lastError prevents Chrome from logging when the extension is absent.
      void runtime.lastError
    })
  }
}
